/**
 * Sales route tree, composable under any parent path.
 *
 * [router.tsx](./router.tsx) mounts it at the repo root for standalone dev;
 * the Vera Shell mounts it under `/sales/*`. The `salesRoutes(basePath)`
 * factory takes the mount prefix so the sidebar's absolute-path `<Link>`s
 * point at the right URLs in each context.
 *
 * Route-object paths stay relative so React Router resolves them against
 * the mount point. `<Navigate to="../X">` inside this tree also resolves
 * correctly because `..` walks the matched route hierarchy, not URL text.
 */

import { lazy, Suspense, createContext, useContext } from 'react';
import { Navigate, Outlet, type RouteObject } from 'react-router-dom';
import { Spinner } from '@fluentui/react-components';
import { ProtectedRoute } from '@shared/auth';
import { AppShell } from '@widgets/app-shell';
import { useIsAdmin, useIsManager } from '@shared/providers';
import { SalesSettings } from '../ui/SalesSettings';
import { ErrorBoundary } from '@shared/ui';
import {
  BoardRegular,
  PersonRegular,
  PipelineRegular,
  CardUiRegular,
  HandshakeRegular,
  BroomRegular,
  BookRegular,
  SettingsRegular,
  PeopleTeamRegular,
  TrophyRegular,
  TaskListLtrRegular,
  CalendarLtrRegular,
  BuildingRegular,
} from '@fluentui/react-icons';
import type { NavItem } from '@widgets/app-shell/ui/AppShell';

const DashboardPage = lazy(() => import('@sales/pages/dashboard').then(m => ({ default: m.DashboardPage })));
const BrokerOverviewPage = lazy(() => import('@sales/pages/broker-overview').then(m => ({ default: m.BrokerOverviewPage })));
const BrokerNewVsExistingPage = lazy(() => import('@sales/pages/broker-new-vs-existing').then(m => ({ default: m.BrokerNewVsExistingPage })));
const BrokerMapPage = lazy(() => import('@sales/pages/broker-map').then(m => ({ default: m.BrokerMapPage })));
const GroupsMapPage = lazy(() => import('@sales/pages/groups-map').then(m => ({ default: m.GroupsMapPage })));
const PipelineTrackerPage = lazy(() => import('@sales/pages/pipeline-tracker').then(m => ({ default: m.PipelineTrackerPage })));
const PipelineOpenCasesPage = lazy(() => import('@sales/pages/pipeline-open-cases').then(m => ({ default: m.PipelineOpenCasesPage })));
const SalespersonScorecardPage = lazy(() => import('@sales/pages/salesperson-scorecard').then(m => ({ default: m.SalespersonScorecardPage })));
const SalespersonOverviewPage = lazy(() => import('@sales/pages/salesperson-scorecard').then(m => ({ default: m.SalespersonOverviewPage })));
const BrokerScorecardPage = lazy(() => import('@sales/pages/broker-scorecard').then(m => ({ default: m.BrokerScorecardPage })));
const OneCardPage = lazy(() => import('@sales/pages/one-card').then(m => ({ default: m.OneCardPage })));
const HappyBirthdayPage = lazy(() => import('@sales/pages/happy-birthday').then(m => ({ default: m.HappyBirthdayPage })));
const ActivityDashboardPage = lazy(() => import('@sales/pages/activity-dashboard').then(m => ({ default: m.ActivityDashboardPage })));
const HousekeepingPage = lazy(() => import('@sales/pages/housekeeping').then(m => ({ default: m.HousekeepingPage })));
const AdminPage = lazy(() => import('@sales/pages/admin').then(m => ({ default: m.AdminPage })));
const SalesManagerPage = lazy(() => import('@sales/pages/sales-manager').then(m => ({ default: m.SalesManagerPage })));
const WonCasesPage = lazy(() => import('@sales/pages/won-cases').then(m => ({ default: m.WonCasesPage })));
const QuickActivityPointsPage = lazy(() => import('@sales/pages/quick-activity-points').then(m => ({ default: m.QuickActivityPointsPage })));
const MonthlyMeetingPage = lazy(() => import('@sales/pages/monthly-meeting').then(m => ({ default: m.MonthlyMeetingPage })));
const GuidePage = lazy(() => import('@sales/pages/guide').then(m => ({ default: m.GuidePage })));
function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Spinner style={{ padding: '2rem' }} label="Loading..." />}>{children}</Suspense>;
}

/**
 * Mount-point prefix for every absolute `<Link>` inside the Sales tree.
 * Empty string when Sales is standalone; `/sales` when mounted in the shell.
 */
const SalesBasePathContext = createContext<string>('');

function buildNavItems(opts: { isAdmin: boolean; isManager: boolean; basePath: string }): NavItem[] {
  const { isAdmin, isManager, basePath } = opts;
  const p = (suffix: string) => `${basePath}${suffix}` || '/';
  const items: NavItem[] = [
    { path: p('/'), label: 'Dashboard', icon: BoardRegular },
    {
      path: p('/brokers'), label: 'Brokers', icon: HandshakeRegular,
      children: [
        { path: p('/broker-overview'), label: 'Overview' },
        { path: p('/broker-detail'), label: 'Detail' },
        { path: p('/broker-new-vs-existing'), label: 'New vs Existing' },
        { path: p('/broker-map'), label: 'Map' },
        { path: p('/happy-birthday'), label: 'Birthdays' },
      ],
    },
    {
      path: p('/groups'), label: 'Groups', icon: BuildingRegular,
      children: [
        { path: p('/groups-map'), label: 'Map' },
      ],
    },
    {
      path: p('/pipeline'), label: 'Pipeline', icon: PipelineRegular,
      children: [
        { path: p('/pipeline-tracker'), label: 'Dashboard' },
        { path: p('/pipeline-open-cases'), label: 'Open Cases' },
      ],
    },
    { path: p('/won-cases'), label: 'Won Cases', icon: TrophyRegular },
    { path: p('/one-card'), label: 'One Card', icon: CardUiRegular },
    {
      path: p('/salespeople'), label: 'Salespeople', icon: PersonRegular,
      children: [
        { path: p('/salesperson-scorecard'), label: 'Scorecard' },
        { path: p('/salesperson-overview'), label: 'Overview' },
        { path: p('/activity-dashboard'), label: 'Activity Tracker' },
      ],
    },
    { path: p('/activity'), label: 'Points', icon: TaskListLtrRegular },
    { path: p('/monthly-meeting'), label: 'Monthly Meeting', icon: CalendarLtrRegular },
    { path: p('/housekeeping'), label: 'Housekeeping', icon: BroomRegular },
  ];
  if (isManager) {
    items.push({ path: p('/sales-manager'), label: 'Sales Manager', icon: PeopleTeamRegular });
  }
  if (isAdmin) {
    items.push({ path: p('/admin'), label: 'Admin', icon: SettingsRegular });
  }
  items.push({ path: p('/guide'), label: 'Guide', icon: BookRegular });
  return items;
}

export function SalesRootLayout() {
  const basePath = useContext(SalesBasePathContext);
  const isAdmin = useIsAdmin('Sales');
  const isManager = useIsManager('Sales');
  const navItems = buildNavItems({ isAdmin, isManager, basePath });
  return (
    <ErrorBoundary>
      <AppShell
        appId="sales"
        appTitle="Vera Sales"
        navItems={navItems}
        headerColor="#553746"
        settingsContent={isAdmin ? <SalesSettings /> : undefined}
      >
        <Outlet />
      </AppShell>
    </ErrorBoundary>
  );
}

export function SalesProtectedLayout() {
  return (
    <ProtectedRoute appRolePrefix="Sales">
      <SalesRootLayout />
    </ProtectedRoute>
  );
}

/**
 * Returns the Sales route tree, with sidebar links prefixed for the given
 * mount path. Standalone: `salesRoutes()` at repo root. Shell: `salesRoutes('/sales')`.
 */
export function salesRoutes(basePath: string = ''): RouteObject[] {
  return [
    {
      element: (
        <SalesBasePathContext.Provider value={basePath}>
          <Outlet />
        </SalesBasePathContext.Provider>
      ),
      children: [
        {
          element: <SalesProtectedLayout />,
          children: [
            { index: true, element: <LazyPage><DashboardPage /></LazyPage> },
            { path: 'salespeople', element: <Navigate to="../salesperson-scorecard" replace /> },
            { path: 'salesperson-scorecard', element: <LazyPage><SalespersonScorecardPage /></LazyPage> },
            { path: 'salesperson-overview', element: <LazyPage><SalespersonOverviewPage /></LazyPage> },
            { path: 'brokers', element: <Navigate to="../broker-overview" replace /> },
            { path: 'broker-scorecard', element: <Navigate to="../broker-overview" replace /> },
            { path: 'broker-overview', element: <LazyPage><BrokerOverviewPage /></LazyPage> },
            { path: 'broker-detail', element: <LazyPage><BrokerScorecardPage /></LazyPage> },
            { path: 'broker-detail/:brokerId', element: <LazyPage><BrokerScorecardPage /></LazyPage> },
            { path: 'broker-new-vs-existing', element: <LazyPage><BrokerNewVsExistingPage /></LazyPage> },
            { path: 'broker-map', element: <LazyPage><BrokerMapPage /></LazyPage> },
            { path: 'groups', element: <Navigate to="../groups-map" replace /> },
            { path: 'groups-map', element: <LazyPage><GroupsMapPage /></LazyPage> },
            { path: 'pipeline', element: <Navigate to="../pipeline-tracker" replace /> },
            { path: 'pipeline-tracker', element: <LazyPage><PipelineTrackerPage /></LazyPage> },
            { path: 'pipeline-open-cases', element: <LazyPage><PipelineOpenCasesPage /></LazyPage> },
            { path: 'one-card', element: <LazyPage><OneCardPage /></LazyPage> },
            { path: 'won-cases', element: <LazyPage><WonCasesPage /></LazyPage> },
            { path: 'activity', element: <LazyPage><QuickActivityPointsPage /></LazyPage> },
            { path: 'monthly-meeting', element: <LazyPage><MonthlyMeetingPage /></LazyPage> },
            { path: 'log-activity', element: <Navigate to="../activity" replace /> },
            { path: 'happy-birthday', element: <LazyPage><HappyBirthdayPage /></LazyPage> },
            { path: 'activity-dashboard', element: <LazyPage><ActivityDashboardPage /></LazyPage> },
            { path: 'housekeeping', element: <LazyPage><HousekeepingPage /></LazyPage> },
            { path: 'admin', element: <LazyPage><AdminPage /></LazyPage> },
            { path: 'sales-manager', element: <LazyPage><SalesManagerPage /></LazyPage> },
            { path: 'guide', element: <LazyPage><GuidePage /></LazyPage> },
          ],
        },
        {
          path: '*',
          element: <Navigate to={basePath || '/'} replace />,
        },
      ],
    },
  ];
}

